/**
 * Wave 29.10.l4 — /clock page (chooser → personal · kiosk).
 *
 * URL params:
 *   /clock                      — chooser landing (Personal | Kiosk cards)
 *   /clock?mode=personal        — personal mode (my own clock in/out, PIN-gated)
 *   /clock?mode=kiosk           — kiosk mode (employee grid, PIN verify)
 *   /clock?mode=kiosk&crew=1    — kiosk with crew-chained (stays open for next worker)
 *
 * Back-compat: /clock?kiosk=1 still maps to mode=kiosk; /clock?crew=1 → kiosk+crew.
 *
 * The clocking module is referred to as "Clocking" in the UI. Internally the
 * "kiosk" terminology is kept because that's the data-testid/audit naming
 * across backend tokens (kiosk_token, kiosk/roster, etc.). What renamed is
 * the user-facing heading only.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  LogIn, LogOut, Coffee, ChevronLeft, ChevronRight, Users as UsersIcon,
  ShieldCheck, AlertTriangle, Loader2, Clock, Briefcase, Lock,
  User, Monitor, KeyRound, Search,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import GpsRequiredModal from "../components/GpsRequiredModal";
import { requestPosition } from "../lib/geo";
import AttendanceClaimCard from "../components/clock/AttendanceClaimCard";
import { fmtTime } from "../lib/datetime";

function _resolveMode(params) {
  // Back-compat: old links used ?kiosk=1 / ?crew=1 directly.
  if (params.get("mode")) return params.get("mode");
  if (params.get("kiosk")) return "kiosk";
  if (params.get("crew")) return "kiosk";
  return null;
}

export default function ClockPage() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const mode = _resolveMode(params);
  const crew = params.get("crew") === "1" || (mode === "kiosk" && params.get("crew") === "1");
  if (!user) return null;

  return (
    <div className="space-y-4" data-testid={`clock-page-${mode || "chooser"}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={mode ? "/clock" : "/"} className="hv-btn hv-btn-ghost text-xs" data-testid="clock-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#1FB6C1] inline-flex items-center gap-1">
          <Clock className="w-3 h-3" /> Clocking{mode ? ` · ${mode}${crew ? " · crew" : ""}` : ""}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {mode && (
            <Link to="/clock" className="text-[10px] text-[#647087] hover:text-white" data-testid="clock-switch-mode">
              ← switch mode
            </Link>
          )}
        </div>
      </div>
      {!mode && <ChooserCard />}
      {mode === "personal" && <PersonalClock />}
      {mode === "kiosk" && <KioskClock chained={crew} />}
    </div>
  );
}

// ─── Chooser landing ───────────────────────────────────────────────
function ChooserCard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const isHr = user && ["owner", "admin", "manager"].includes(user.role);
  return (
    <div className="max-w-3xl mx-auto space-y-4">
      <div className="text-center">
        <h1 className="font-display text-2xl sm:text-3xl font-bold mb-1">How are you clocking?</h1>
        <p className="text-xs text-[#9aa3b5]">Pick one. You can switch any time.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => nav("/clock?mode=personal")}
          className="hv-card p-6 text-left transition-all hover:border-[#1FB6C1]/50 hover:bg-white/[0.04] group"
          data-testid="clocking-choose-personal"
        >
          <User className="w-7 h-7 text-[#1FB6C1] mb-3" />
          <div className="font-display text-lg font-bold text-white mb-1 flex items-center gap-1">
            Personal <ChevronRight className="w-4 h-4 text-[#647087] group-hover:translate-x-1 transition-transform" />
          </div>
          <div className="text-[11px] text-[#9aa3b5] leading-relaxed">
            Clock in as yourself, on your own device. You'll be asked for your 4-digit PIN even though you're signed in — keeps the audit honest.
          </div>
        </button>
        <button
          type="button"
          onClick={() => nav("/clock?mode=kiosk")}
          className="hv-card p-6 text-left transition-all hover:border-[#A78BFA]/50 hover:bg-white/[0.04] group"
          data-testid="clocking-choose-kiosk"
        >
          <Monitor className="w-7 h-7 text-[#A78BFA] mb-3" />
          <div className="font-display text-lg font-bold text-white mb-1 flex items-center gap-1">
            Kiosk <ChevronRight className="w-4 h-4 text-[#647087] group-hover:translate-x-1 transition-transform" />
          </div>
          <div className="text-[11px] text-[#9aa3b5] leading-relaxed">
            Shared device — anyone on the team can clock in. Pick your name, type your 4-digit PIN. Optional crew mode keeps the screen open for the next worker.
          </div>
        </button>
      </div>
      {isHr && (
        <div className="text-center pt-1">
          <Link to="/team/attendance" className="text-[11px] text-[#647087] hover:text-white" data-testid="clocking-attendance-link">
            View attendance register →
          </Link>
        </div>
      )}
    </div>
  );
}

// ─── Personal mode ─────────────────────────────────────────────────
function PersonalClock() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pin, setPin] = useState("");
  const [showPinManager, setShowPinManager] = useState(false);
  const [gpsReq, setGpsReq] = useState(null);  // { kind } | null
  // Iter 231.5 — persistent last-failure banner so the tech can SEE
  // why their tap "didn't go through" instead of relying on a toast
  // that vanishes in 4 seconds. Cleared on the next successful clock.
  const [lastError, setLastError] = useState(null);  // { msg, hint, code }
  // GPS preflight pill so the user knows at a glance whether their
  // browser will be able to capture location BEFORE they tap Clock.
  const [gpsPerm, setGpsPerm] = useState("unknown");  // granted | denied | prompt | unsupported | unknown
  const pinRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/team/attendance/me");
      setStatus(data);
    } catch (_e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { pinRef.current?.focus(); }, []);

  // Iter 231.5 — probe geolocation permission on mount so the UI can
  // tell the tech BEFORE they tap Clock if their device will refuse.
  useEffect(() => {
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setGpsPerm("unsupported");
      return;
    }
    if (!navigator.permissions || !navigator.permissions.query) {
      setGpsPerm("unknown");
      return;
    }
    navigator.permissions.query({ name: "geolocation" }).then(
      (p) => {
        setGpsPerm(p.state);
        p.onchange = () => setGpsPerm(p.state);
      },
      () => setGpsPerm("unknown"),
    );
  }, []);

  const clock = async (kind) => {
    if (busy) return;
    if (!status?.pin_set) {
      setShowPinManager(true);
      setLastError({ msg: "Set your 4-digit PIN first.", hint: "Use the form below.", code: "no_pin" });
      toast.error("Set your 4-digit PIN first.");
      return;
    }
    if (!/^\d{4}$/.test(pin)) {
      setLastError({ msg: "Enter your 4-digit PIN.", hint: "Tap the box above and type your PIN.", code: "bad_pin" });
      toast.error("Enter your 4-digit PIN to confirm.");
      return;
    }
    // GPS is mandatory — open the blocking modal and wait for a fix.
    setLastError(null);
    setGpsReq({ kind });
  };

  const _completeClock = async (kind, loc) => {
    setBusy(true);
    try {
      const v = await api.post("/team/attendance/auth/pin/verify", { user_id: status.user_id, pin });
      const token = v.data.kiosk_token;
      const body = { kind, kiosk_token: token, ...loc };
      // Iter 231.5 — when the tech opts in to "clock without GPS",
      // `loc` is { denied: true } and lat/lng are absent. Send that
      // explicitly so the backend stamps the event with denied=true
      // and HR can see it in the exceptions tab.
      const { data } = await api.post("/team/attendance/clock", body);
      const where = data.jc_attached
        ? `on site at ${data.jc_attached.customer_name || data.jc_attached.short_id}`
        : data.out_of_zone ? "off-site (flagged)"
        : loc?.denied ? "without GPS (flagged for HR)"
        : "at office";
      toast.success(`Clocked ${kind === "in" ? "in" : kind === "out" ? "out" : kind.replace("_", " ")} · ${where}`);
      setPin("");
      setLastError(null);  // clear stale errors after a successful clock
      load();
    } catch (e) {
      // Iter 231.5 — also pin the error to the screen so the tech can
      // read it AFTER the toast has gone away. Friendlier copy depending
      // on the failure type.
      const detail = e?.response?.data?.detail || e?.message || "Clock event failed.";
      const isNetwork = !e?.response;
      setLastError({
        msg: isNetwork ? "Couldn't reach the server." : detail,
        hint: isNetwork
          ? "Check your signal and tap Clock again. If you stay offline, use \"Submit amendment\" below — your manager will fix it."
          : "Tap Clock again. If it keeps failing, submit an amendment below.",
        code: isNetwork ? "network" : (e?.response?.status || "error").toString(),
      });
      toast.error(detail);
    } finally { setBusy(false); }
  };

  if (!status) return <div className="h-40 bg-white/[0.04] rounded animate-pulse" />;
  const isIn = status.status === "in";
  const onBreak = status.status === "break_start";
  return (
    <div className="max-w-md mx-auto space-y-4">
      <div className="hv-card p-5 text-center">
        <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">Today</div>
        <div className="font-display text-5xl font-bold tabular-nums">
          {Math.floor((status.today_minutes || 0) / 60)}h {(status.today_minutes || 0) % 60}m
        </div>
        <div className="text-[11px] text-[#a0aabf] mt-2 inline-flex items-center gap-1 justify-center flex-wrap">
          <Clock className="w-3 h-3" /> Status: <span className={isIn ? "text-emerald-300" : onBreak ? "text-amber-300" : "text-[#647087]"}>{status.status}</span>
          {status.last_event?.jc_id && <span className="text-[#1FB6C1]"> · linked to JC</span>}
          {/* Iter 231.5 — GPS preflight pill */}
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider border ${
              gpsPerm === "granted" ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
              : gpsPerm === "denied" || gpsPerm === "unsupported" ? "border-rose-500/40 text-rose-300 bg-rose-500/5"
              : "border-amber-400/30 text-amber-300 bg-amber-400/5"
            }`}
            data-testid="clock-gps-pill"
            title={gpsPerm === "denied" ? "Location is blocked on your device. Allow it before tapping Clock."
              : gpsPerm === "unsupported" ? "This browser cannot share location."
              : gpsPerm === "granted" ? "Location is allowed — Clock will work."
              : "Location permission will be asked when you tap Clock."}
          >
            <Lock className="w-2.5 h-2.5" />
            GPS: {gpsPerm === "granted" ? "ok" : gpsPerm === "denied" ? "blocked" : gpsPerm === "unsupported" ? "n/a" : "will ask"}
          </span>
        </div>
      </div>

      {/* Iter 231.5 — persistent last-failure banner. Stays on-screen
          until the next successful clock, so techs can SEE why the
          previous tap didn't go through (instead of guessing). */}
      {lastError && (
        <div
          className="hv-card p-3 border border-amber-400/40 bg-amber-400/5 flex items-start gap-2"
          data-testid="clock-last-error"
        >
          <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1 text-xs">
            <div className="font-medium text-amber-100">{lastError.msg}</div>
            {lastError.hint && (
              <div className="text-[11px] text-[#cbd2df] mt-0.5">{lastError.hint}</div>
            )}
            <div className="text-[10px] text-[#647087] mt-1 font-mono">code: {lastError.code}</div>
          </div>
          <button
            type="button"
            onClick={() => setLastError(null)}
            className="text-[10px] uppercase tracking-wider text-[#647087] hover:text-white"
            data-testid="clock-last-error-dismiss"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* PIN entry — always shown so personal mode is gated even when signed in */}
      {status.pin_set ? (
        <div className="hv-card p-4">
          <label className="text-[10px] uppercase tracking-[0.2em] text-[#647087] flex items-center gap-1 mb-2">
            <Lock className="w-3 h-3" /> Confirm with your 4-digit PIN
          </label>
          <input
            ref={pinRef}
            type="password"
            inputMode="numeric"
            maxLength={4}
            autoFocus
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
            className="hv-input text-center text-2xl tracking-[0.5em] w-full"
            placeholder="••••"
            data-testid="personal-pin"
          />
        </div>
      ) : (
        <PinSetupCard onDone={() => { setShowPinManager(false); load(); }} forceOpen />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {!isIn && !onBreak && (
          <button onClick={() => clock("in")} disabled={busy || pin.length !== 4 || !status.pin_set} className="hv-btn hv-btn-primary text-base py-4 disabled:opacity-50 sm:col-span-2" data-testid="clock-in">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogIn className="w-5 h-5" />} Clock in
          </button>
        )}
        {isIn && (
          <>
            <button onClick={() => clock("out")} disabled={busy || pin.length !== 4} className="hv-btn text-base py-4 border border-red-500/40 text-red-200 hover:bg-red-500/10 disabled:opacity-50" data-testid="clock-out">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <LogOut className="w-5 h-5" />} Clock out
            </button>
            <button onClick={() => clock("break_start")} disabled={busy || pin.length !== 4} className="hv-btn hv-btn-ghost text-base py-4 disabled:opacity-50" data-testid="break-start">
              <Coffee className="w-5 h-5" /> Start break
            </button>
          </>
        )}
        {onBreak && (
          <button onClick={() => clock("break_end")} disabled={busy || pin.length !== 4} className="hv-btn hv-btn-primary text-base py-4 disabled:opacity-50 sm:col-span-2" data-testid="break-end">
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Coffee className="w-5 h-5" />} End break
          </button>
        )}
      </div>

      {/* Always-available "change my PIN" link */}
      {status.pin_set && (
        <div className="text-center">
          <button
            type="button"
            onClick={() => setShowPinManager((v) => !v)}
            className="text-[11px] text-[#647087] hover:text-white inline-flex items-center gap-1"
            data-testid="personal-pin-manager-toggle"
          >
            <KeyRound className="w-3 h-3" /> {showPinManager ? "Hide PIN manager" : "Change my PIN"}
          </button>
        </div>
      )}
      {showPinManager && status.pin_set && <PinSetupCard onDone={() => { setShowPinManager(false); load(); }} />}

      {/* Iter 181 — claim a forgotten/wrong clock event */}
      <AttendanceClaimCard onSubmitted={() => load()} />

      {status.today_events?.length > 0 && (
        <div className="hv-card p-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-2">Today's events</div>
          <ul className="space-y-1 text-xs">
            {status.today_events.map((e) => (
              <li key={e.id} className="flex items-center justify-between gap-2 py-1 border-b border-white/5 last:border-b-0">
                <span className={e.kind === "in" ? "text-emerald-300" : e.kind === "out" ? "text-red-300" : "text-amber-300"}>{e.kind.replace("_", " ")}</span>
                <span className="text-[#a0aabf]">{fmtTime(e.at)}</span>
                {e.out_of_zone && <span className="text-[10px] text-amber-300 inline-flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> off-site</span>}
                {e.jc_id && <span className="text-[10px] text-[#1FB6C1] inline-flex items-center gap-0.5"><Briefcase className="w-3 h-3" /> JC</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
      {gpsReq && (
        <GpsRequiredModal
          open
          title={`Allow GPS to clock ${gpsReq.kind === "in" ? "in" : gpsReq.kind === "out" ? "out" : gpsReq.kind.replace("_", " ")}`}
          reason="Your clock-in/out location helps managers verify attendance. If GPS isn't available, you can still clock — your event will be flagged for HR review."
          onCaptured={async (loc) => {
            const k = gpsReq.kind;
            setGpsReq(null);
            await _completeClock(k, loc);
          }}
          onSkipped={async (reason) => {
            // Iter 231.5 — confirmed "clock without GPS" path. Posts the
            // event with denied=true (no lat/lng) so HR sees it flagged.
            const k = gpsReq.kind;
            setGpsReq(null);
            await _completeClock(k, { denied: true, source: "no_gps", error: reason });
          }}
          onCancel={(reason) => {
            setGpsReq(null);
            // Iter 231.5 — pin a persistent banner so the tech can see
            // WHY the clock didn't go through after they dismiss the modal.
            const r = String(reason || "cancelled");
            if (r === "permission_denied") {
              setLastError({
                msg: "Location is blocked on your device.",
                hint: "Tap the lock icon in your address bar → Permissions → Location → Allow. Then tap Clock again.",
                code: "gps_denied",
              });
            } else if (r === "geolocation_timeout") {
              setLastError({
                msg: "GPS took too long to get a fix.",
                hint: "Move outside or near a window and tap Clock again. If you stay indoors, submit an amendment below.",
                code: "gps_timeout",
              });
            } else if (r === "geolocation_unsupported") {
              setLastError({
                msg: "Your browser doesn't expose GPS.",
                hint: "Use a recent Chrome or Safari, or submit an amendment below.",
                code: "gps_unsupported",
              });
            } else if (r === "cancelled" || r === "asking") {
              // user cancelled the modal explicitly — leave a soft hint
              setLastError({
                msg: "Clock cancelled — GPS is required.",
                hint: "Tap Clock again and allow location. If you can't, submit an amendment below.",
                code: "gps_cancelled",
              });
            } else {
              setLastError({
                msg: "Couldn't capture GPS.",
                hint: "Step outside or near a window and try again. Or submit an amendment below.",
                code: "gps_failed",
              });
            }
          }}
        />
      )}
    </div>
  );
}

// ─── PIN setup / change card (re-used by personal + admin reset) ────
function PinSetupCard({ onDone, forceOpen = false }) {
  const [pin, setPin] = useState("");
  const [pin2, setPin2] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!/^\d{4}$/.test(pin)) return toast.error("PIN must be exactly 4 digits.");
    if (pin !== pin2) return toast.error("PINs don't match.");
    setBusy(true);
    try {
      await api.post("/team/attendance/auth/pin", { pin });
      toast.success("PIN saved.");
      setPin(""); setPin2("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save PIN.");
    } finally { setBusy(false); }
  };
  return (
    <div className={"hv-card p-4 " + (forceOpen ? "border-amber-400/40 bg-amber-400/[0.05]" : "")} data-testid="personal-pin-setup">
      <div className="text-sm font-medium text-white inline-flex items-center gap-1 mb-1">
        <KeyRound className="w-3.5 h-3.5 text-[#1FB6C1]" /> {forceOpen ? "Set your 4-digit PIN" : "Change your 4-digit PIN"}
      </div>
      <p className="text-[11px] text-[#a0aabf] mb-2">
        4 digits. Used to confirm clocking events — separate from your login password.
      </p>
      <div className="flex flex-col sm:flex-row gap-2">
        <input type="password" inputMode="numeric" maxLength={4} value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          className="hv-input text-center text-lg tracking-[0.4em] w-full sm:w-32"
          placeholder="New PIN" data-testid="pin-input-new" />
        <input type="password" inputMode="numeric" maxLength={4} value={pin2}
          onChange={(e) => setPin2(e.target.value.replace(/\D/g, "").slice(0, 4))}
          className="hv-input text-center text-lg tracking-[0.4em] w-full sm:w-32"
          placeholder="Confirm" data-testid="pin-input-confirm" />
        <button onClick={save} disabled={busy || pin.length !== 4 || pin2.length !== 4}
          className="hv-btn hv-btn-primary text-xs disabled:opacity-50"
          data-testid="pin-save">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Save PIN"}
        </button>
      </div>
    </div>
  );
}

// ─── Kiosk mode (with optional crew chaining) ───────────────────────
function KioskClock({ chained: initialChained }) {
  const [roster, setRoster] = useState([]);
  const [picked, setPicked] = useState(null);
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  // Iter 349.g — GPS modal removed from kiosk flow. Best-effort GPS
  // now happens silently inside `submit()`; no blocking modal.
  const [q, setQ] = useState("");
  const [crewMode, setCrewMode] = useState(initialChained);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [adminPickedId, setAdminPickedId] = useState("");
  const { user } = useAuth();
  const pinRef = useRef(null);
  const isHr = user && ["owner", "admin", "manager"].includes(user.role);

  const load = async () => {
    try {
      const { data } = await api.get("/team/attendance/kiosk/roster");
      setRoster(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load roster.");
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { if (picked) setTimeout(() => pinRef.current?.focus(), 100); }, [picked]);
  useEffect(() => {
    if (!picked && !confirm) return;
    const t = setTimeout(() => { setPicked(null); setPin(""); setConfirm(null); }, 60000);
    return () => clearTimeout(t);
  }, [picked, confirm]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase().trim();
    const base = s ? roster.filter((r) => (r.name || "").toLowerCase().includes(s)) : roster;
    // Wave 29 Chunk 10m — sort kiosk roster by activity:
    //   1. Currently clocked-in (most recent clock-in first)
    //   2. On break
    //   3. Off shift (alphabetical)
    const rank = (r) => {
      if (r.current_status === "in") return 0;
      if (r.current_status === "break_start") return 1;
      return 2;
    };
    return [...base].sort((a, b) => {
      const ra = rank(a);
      const rb = rank(b);
      if (ra !== rb) return ra - rb;
      // Within "in" / "break", sort by most-recent event first.
      if (ra < 2) {
        const ta = a.current_since || a.last_event_at || "";
        const tb = b.current_since || b.last_event_at || "";
        if (ta !== tb) return tb.localeCompare(ta);
      }
      return (a.name || "").localeCompare(b.name || "");
    });
  }, [q, roster]);

  const submit = async (kind) => {
    if (!picked || pin.length !== 4 || busy) return;
    // Iter 349.g (2026-02-25) — Kiosk clock-in was silently stalling on
    // the GpsRequiredModal ("nothing happens when he clicks Clock in"
    // per Dirk report). A wall-mounted kiosk is by definition at a
    // fixed location, so requiring a per-clock GPS fix (which often
    // times out or is denied at the browser/OS level on shared shop
    // tablets) provides zero value and blocks the whole flow.
    //
    // New flow — kiosk mode only:
    //   1. Best-effort GPS capture with a short (4s) timeout, silent
    //      — no user-facing modal.
    //   2. Fire the clock POST regardless. If GPS resolved, include
    //      lat/lng. If not, post `denied: true, source: "kiosk_no_gps"`
    //      so the backend accepts and HR sees the flag in exceptions.
    //
    // Personal-mode clocking still uses the full GPS modal (that flow
    // benefits from location — the tech might be off-site).
    setBusy(true);
    try {
      const loc = await requestPosition({ timeoutMs: 4000, highAccuracy: false });
      const payload = loc.denied
        ? { denied: true, source: "kiosk_no_gps", error: loc.error || "kiosk_gps_failed" }
        : { lat: loc.lat, lng: loc.lng, accuracy_m: loc.accuracy_m, source: "kiosk" };
      await _doKioskClock(kind, payload);
    } finally {
      // _doKioskClock also flips busy but this belt-and-braces reset
      // handles the case where the outer try throws before it runs.
      setBusy(false);
    }
  };

  const _doKioskClock = async (kind, loc) => {
    setBusy(true);
    try {
      const v = await api.post("/team/attendance/auth/pin/verify", { user_id: picked.id, pin });
      const token = v.data.kiosk_token;
      const { data } = await api.post("/team/attendance/clock", { kind, kiosk_token: token, ...loc });
      setConfirm({ user: picked, kind, event: data.event });
      setPicked(null); setPin("");
      load();
      setTimeout(() => setConfirm(null), crewMode ? 2500 : 3500);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed.");
    } finally { setBusy(false); }
  };

  if (confirm) {
    return (
      <div className="hv-card p-10 text-center" data-testid="kiosk-confirmation">
        <ShieldCheck className="w-12 h-12 text-emerald-300 mx-auto mb-3" />
        <div className="text-2xl font-display font-bold">{confirm.user.name}</div>
        <div className="text-sm text-[#a0aabf] mt-1">Clocked {confirm.kind === "in" ? "in" : confirm.kind === "out" ? "out" : confirm.kind.replace("_", " ")} at {fmtTime(confirm.event.at)}</div>
        {confirm.event.out_of_zone && (
          <div className="text-[11px] text-amber-300 mt-2 inline-flex items-center gap-1 justify-center">
            <AlertTriangle className="w-3 h-3" /> Off-site — flagged for HR review
          </div>
        )}
      </div>
    );
  }
  if (picked) {
    const isIn = picked.current_status === "in";
    const onBreak = picked.current_status === "break_start";
    return (
      <div className="hv-card p-6 text-center max-w-sm mx-auto relative" data-testid="kiosk-pin-screen">
        <button onClick={() => { setPicked(null); setPin(""); }} className="absolute top-3 left-3 text-[10px] text-[#647087] hover:text-white">← Back</button>
        <div className="text-xl font-display font-bold mb-1 mt-2">{picked.name}</div>
        <div className="text-xs text-[#a0aabf] mb-4">Enter your 4-digit PIN</div>
        <input
          ref={pinRef}
          type="password"
          inputMode="numeric"
          maxLength={4}
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          className="hv-input text-center text-3xl tracking-[0.5em] w-48 mx-auto"
          placeholder="••••"
          data-testid="kiosk-pin"
        />
        <div className="flex gap-2 mt-4 justify-center flex-wrap">
          {!isIn && !onBreak && (
            <button onClick={() => submit("in")} disabled={pin.length !== 4 || busy} className="hv-btn hv-btn-primary text-sm disabled:opacity-50" data-testid="kiosk-confirm-in">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogIn className="w-3.5 h-3.5" />} Clock in
            </button>
          )}
          {isIn && (
            <>
              <button onClick={() => submit("out")} disabled={pin.length !== 4 || busy} className="hv-btn text-sm border border-red-500/40 text-red-200 hover:bg-red-500/10 disabled:opacity-50" data-testid="kiosk-confirm-out">
                <LogOut className="w-3.5 h-3.5" /> Clock out
              </button>
              <button onClick={() => submit("break_start")} disabled={pin.length !== 4 || busy} className="hv-btn hv-btn-ghost text-sm disabled:opacity-50" data-testid="kiosk-confirm-break-start">
                <Coffee className="w-3.5 h-3.5" /> Break
              </button>
            </>
          )}
          {onBreak && (
            <button onClick={() => submit("break_end")} disabled={pin.length !== 4 || busy} className="hv-btn hv-btn-primary text-sm disabled:opacity-50" data-testid="kiosk-confirm-break-end">
              <Coffee className="w-3.5 h-3.5" /> End break
            </button>
          )}
        </div>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-3.5 h-3.5 text-[#647087] absolute left-2 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name…"
            className="hv-input text-sm w-full pl-7"
            data-testid="kiosk-search"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 text-[11px] text-[#9aa3b5] cursor-pointer hover:text-white">
          <input
            type="checkbox"
            checked={crewMode}
            onChange={(e) => setCrewMode(e.target.checked)}
            data-testid="kiosk-crew-toggle"
          />
          <UsersIcon className="w-3 h-3" /> Crew mode (stay open for next worker)
        </label>
        {isHr && (
          <button
            type="button"
            onClick={() => setAdminPanelOpen((v) => !v)}
            className="text-[11px] text-[#647087] hover:text-white inline-flex items-center gap-1"
            data-testid="kiosk-admin-toggle"
            title="Owner / admin / manager: reset a worker's PIN"
          >
            <KeyRound className="w-3 h-3" /> Admin: reset a PIN
          </button>
        )}
      </div>
      {adminPanelOpen && isHr && <AdminPinReset roster={roster} initialUserId={adminPickedId} onDone={() => { setAdminPanelOpen(false); setAdminPickedId(""); load(); }} />}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2" data-testid="kiosk-roster">
        {filtered.map((r) => {
          const isIn = r.current_status === "in";
          const onBreak = r.current_status === "break_start";
          const handleClick = () => {
            if (r.pin_set) { setPicked(r); return; }
            // No PIN yet. For HR users, auto-open the admin panel pre-selected
            // to this worker — turns the dead-end into a 1-click flow.
            if (isHr) {
              setAdminPickedId(r.id);
              setAdminPanelOpen(true);
              setTimeout(() => {
                document.querySelector('[data-testid="kiosk-admin-pin-reset"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
              }, 50);
              return;
            }
            toast.error(`${r.name} hasn't set a PIN yet. Ask an owner / admin / manager to set one.`);
          };
          return (
            <button
              key={r.id}
              type="button"
              onClick={handleClick}
              className={`hv-card p-3 text-left transition-colors ${r.pin_set ? "hover:border-[#1FB6C1]/40" : (isHr ? "hover:border-amber-400/40" : "opacity-50 cursor-not-allowed")}`}
              data-testid={`kiosk-pick-${r.id}`}
            >
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-white/5 overflow-hidden flex items-center justify-center shrink-0">
                  {r.avatar_url ? <img src={r.avatar_url} alt="" className="w-full h-full object-cover" /> : <span className="text-[#a0aabf] text-xs font-semibold">{(r.name || "?").slice(0, 2).toUpperCase()}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white truncate">{r.name}</div>
                  <div className="text-[10px] flex items-center gap-1">
                    {isIn ? <span className="text-emerald-300">● clocked in</span> : onBreak ? <span className="text-amber-300">● on break</span> : <span className="text-[#647087]">off shift</span>}
                  </div>
                </div>
              </div>
              {!r.pin_set && (
                <div className="text-[10px] text-amber-300 mt-1">
                  {isHr ? "No PIN yet — tap to set" : "No PIN yet"}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Admin / manager PIN reset panel (visible inside Kiosk mode) ────
function AdminPinReset({ roster, initialUserId = "", onDone }) {
  const [pickedId, setPickedId] = useState(initialUserId);
  const [newPin, setNewPin] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (initialUserId) setPickedId(initialUserId); }, [initialUserId]);
  const picked = useMemo(() => roster.find((r) => r.id === pickedId), [roster, pickedId]);

  const reset = async (mode) => {
    if (!pickedId) return toast.error("Pick a worker first.");
    if (mode === "set" && !/^\d{4}$/.test(newPin)) return toast.error("New PIN must be 4 digits.");
    setBusy(true);
    try {
      const body = mode === "set" ? { user_id: pickedId, new_pin: newPin } : { user_id: pickedId };
      const { data } = await api.post("/team/attendance/auth/pin/admin-reset", body);
      toast.success(
        data.mode === "set"
          ? `${data.user_name}'s PIN set to ${newPin}.`
          : `${data.user_name}'s PIN cleared — they must set a fresh one.`,
      );
      setNewPin(""); setPickedId("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not reset PIN.");
    } finally { setBusy(false); }
  };

  return (
    <div className="hv-card p-4 border-amber-400/30 bg-amber-400/[0.04]" data-testid="kiosk-admin-pin-reset">
      <div className="text-sm font-medium text-white inline-flex items-center gap-1 mb-1">
        <KeyRound className="w-3.5 h-3.5 text-amber-300" /> Reset a worker's PIN
      </div>
      <p className="text-[11px] text-[#a0aabf] mb-3">
        For when someone forgets their PIN. Either type a new 4-digit code for them, or clear it so they have to set a fresh one themselves.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <select
          value={pickedId}
          onChange={(e) => setPickedId(e.target.value)}
          className="hv-input text-sm"
          data-testid="kiosk-admin-pick-user"
        >
          <option value="">— pick worker —</option>
          {roster.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} {r.pin_set ? "" : "(no PIN)"}
            </option>
          ))}
        </select>
        <input
          type="text"
          inputMode="numeric"
          maxLength={4}
          value={newPin}
          onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
          placeholder="New 4-digit PIN"
          className="hv-input text-sm text-center tracking-[0.4em]"
          data-testid="kiosk-admin-new-pin"
        />
        <div className="flex gap-2">
          <button
            onClick={() => reset("set")}
            disabled={busy || !pickedId || newPin.length !== 4}
            className="hv-btn hv-btn-primary text-xs flex-1 disabled:opacity-50"
            data-testid="kiosk-admin-set-pin"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Set PIN"}
          </button>
          <button
            onClick={() => reset("clear")}
            disabled={busy || !pickedId}
            className="hv-btn text-xs border border-red-500/40 text-red-200 hover:bg-red-500/10 disabled:opacity-50 flex-1"
            data-testid="kiosk-admin-clear-pin"
            title="Clear so the worker sets it themselves"
          >
            Clear
          </button>
        </div>
      </div>
      {picked && (
        <div className="text-[10px] text-[#647087] mt-2">
          Affecting: <b className="text-white">{picked.name}</b> — currently {picked.pin_set ? "has a PIN" : "has no PIN"}.
        </div>
      )}
    </div>
  );
}
